// src/pages/EditDealer.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Dealer {
  id?: number; // Optional for new entries, but fetched entries have an id
  company_name: string;
  created_at?: string;
  country_id?: number;
  hidden?: boolean;
}

const EditDealer: React.FC = () => {
  // Get the country ID from route parameters
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [newDealer, setNewDealer] = useState<Dealer>({ company_name: "" });
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  // State for deletion confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dealerToDelete, setDealerToDelete] = useState<Dealer | null>(null);
  const [confirmText, setConfirmText] = useState("");

  // Fetch existing dealers for this country on mount
  useEffect(() => {
    if (id) {
      fetch(`/api/countries/${id}/dealers`)
        .then((res) => res.json())
        .then((data) => setDealers(data))
        .catch((err) => setError("Error fetching dealers"));
    }
  }, [id]);

  // Listen for any click to dismiss the "Updated" popup when it's shown
  useEffect(() => {
    if (showPopup) {
      const handleDocumentClick = () => setShowPopup(false);
      document.addEventListener("click", handleDocumentClick);
      return () => document.removeEventListener("click", handleDocumentClick);
    }
  }, [showPopup]);

  // Update a specific dealer's company name in state
  const handleDealerChange = (dealerId: number, value: string) => {
    setDealers((prev) =>
      prev.map((dealer) =>
        dealer.id === dealerId ? { ...dealer, company_name: value } : dealer
      )
    );
  };

  // Update the new dealer row
  const handleNewDealerChange = (value: string) => {
    setNewDealer({ company_name: value });
  };

  // Save an existing dealer via PUT
  const handleUpdate = async (dealer: Dealer) => {
    if (!dealer.id) return;
    try {
      const response = await fetch(`/api/dealers/${dealer.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: dealer.company_name,
          countryId: Number(id),
          hidden: dealer.hidden || false,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error updating dealer");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Save the new dealer via POST
  const handleNewSave = async () => {
    if (newDealer.company_name.trim() === "") return;
    try {
      const response = await fetch("/api/dealers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: newDealer.company_name,
          countryId: Number(id),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error adding dealer");
      } else {
        const createdDealer = await response.json();
        setDealers((prev) => [createdDealer, ...prev]);
        setNewDealer({ company_name: "" });
        setError("");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Delete a dealer using the DELETE endpoint
  const handleDeleteDealer = async (dealer: Dealer) => {
    if (!dealer.id) return;
    try {
      const response = await fetch(`/api/dealers/${dealer.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error deleting dealer");
      } else {
        // Remove the dealer from local state after successful deletion
        setDealers((prev) => prev.filter((d) => d.id !== dealer.id));
        setShowDeleteModal(false);
        setDealerToDelete(null);
        setConfirmText("");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Open the delete confirmation modal for a specific dealer
  const openDeleteModal = (dealer: Dealer) => {
    setDealerToDelete(dealer);
    setConfirmText("");
    setShowDeleteModal(true);
  };

  // Update all dealers (existing and new) then refresh the page
  const handleUpdateAll = async () => {
    if (newDealer.company_name.trim() !== "") {
      await handleNewSave();
    }
    for (const dealer of dealers) {
      if (dealer.company_name.trim() !== "") {
        await handleUpdate(dealer);
      }
    }
    setShowPopup(true);
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    backgroundColor: "#F0F5F0",
    minHeight: "100vh",
    padding: "20px",
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "600px",
    margin: "50px auto",
    padding: "30px",
  };

  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px",
  };

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "8px",
    borderBottom: "1px solid #ccc",
    color: "#e41e26",
  };

  const tdStyle: React.CSSProperties = {
    padding: "8px",
    borderBottom: "1px solid #ccc",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "5px",
    boxSizing: "border-box",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "10px 20px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    marginRight: "10px",
  };

  const navContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  };

  const popupStyle: React.CSSProperties = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#e41e26",
    color: "#fff",
    padding: "15px 30px",
    borderRadius: "4px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
    zIndex: 1000,
  };

  const trashButtonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
  };

  // Modal overlay styles for delete confirmation
  const modalOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  // Modal content styles for delete confirmation
  const modalContentStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    maxWidth: "400px",
    width: "90%",
    textAlign: "center",
  };

  return (
    <div style={containerStyle}>
      <Navbar />
      {showPopup && (
        <div style={popupStyle} onClick={() => setShowPopup(false)}>
          Updated
        </div>
      )}
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Dealer</h1>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Dealer</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {dealers.map((dealer) => (
              <tr key={dealer.id}>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={dealer.company_name}
                    onChange={(e) => handleDealerChange(dealer.id!, e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <button style={trashButtonStyle} onClick={() => openDeleteModal(dealer)}>
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            {/* New dealer entry row */}
            <tr>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newDealer.company_name}
                  onChange={(e) => handleNewDealerChange(e.target.value)}
                  style={inputStyle}
                  placeholder="New Dealer"
                />
              </td>
              <td style={tdStyle}></td>
            </tr>
          </tbody>
        </table>
        <div style={navContainerStyle}>
          <button style={buttonStyle} onClick={() => navigate(-1)}>
            Back
          </button>
          <button style={buttonStyle} onClick={handleUpdateAll}>
            Update
          </button>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && dealerToDelete && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete <strong>{dealerToDelete.company_name}</strong>?<br />
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{ width: "80%", padding: "8px", margin: "10px 0" }}
            />
            <div style={{ marginTop: "10px" }}>
              <button
                style={buttonStyle}
                onClick={() => {
                  if (confirmText === "DELETE") {
                    handleDeleteDealer(dealerToDelete);
                  } else {
                    alert("Please type DELETE to confirm.");
                  }
                }}
              >
                Submit
              </button>
              <button style={{ ...buttonStyle, marginLeft: "10px" }} onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDealer;
